import type { ModuleManifest, PluginManifest } from '@gzl10/nexus-sdk'

/**
 * Module source type
 */
export type ModuleSource = 'core' | 'plugin' | 'standalone'

/**
 * Registered module with source metadata
 */
export interface RegisteredModule extends ModuleManifest {
  /** Module source */
  _source: ModuleSource
  /** Plugin name (only if _source === 'plugin') */
  _pluginName?: string
  /** If true, module routes won't be mounted (services still available via init) */
  _disableEndpoints?: boolean
  /** Table name prefix for plugin modules (e.g. 'sch_') */
  _tablePrefix?: string
  /** Original (unprefixed) table names declared by this plugin */
  _pluginTables?: Set<string>
}

/**
 * In-memory engine state.
 * IMPORTANT: This file DOES NOT import modules to avoid circular dependencies.
 */
export const moduleStore = {
  /** Registered modules with source metadata */
  modules: [] as RegisteredModule[],

  /** Registered plugins (by name) */
  plugins: new Map<string, PluginManifest>(),

  /** Registered plugins (by 3-char code) */
  pluginsByCode: new Map<string, PluginManifest>(),

  /** Registered tables (for uniqueness validation) */
  tables: new Set<string>(),

  /** Registered subjects (for uniqueness validation) */
  subjects: new Set<string>(['all']),

  /** Map of table names to their CASL subjects */
  tableToSubject: new Map<string, string>()
}

/**
 * Resets the store to its initial state.
 * Used by stop() to allow clean restarts.
 */
export function resetStore(): void {
  moduleStore.modules.length = 0
  moduleStore.plugins.clear()
  moduleStore.pluginsByCode.clear()
  moduleStore.tables.clear()
  moduleStore.subjects.clear()
  moduleStore.subjects.add('all')
  moduleStore.tableToSubject.clear()
}
